import { Autocomplete, TextField, Typography } from "@mui/material";
import naicsCodes from "encamp-shared/src/constants/naics.json";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import React, { forwardRef, useMemo } from "react";
import { ControllerFieldState } from "react-hook-form";
import { DisplayOnlyField } from "./Forms/DisplayOnlyField";
import { ErrorDisplay } from "./Forms/ErrorDisplay";

type Props = {
  onChange: (naicsCode: number | null) => void;
  value: number | null;
  disabled?: boolean;
} & ControllerFieldState;

type NaicsCode = {
  code: number;
  description: string;
};

export const NaicsPicker: React.FC<Props> = forwardRef(function NaicsPicker(
  { onChange, value, error, disabled },
  ref
) {
  // Find the NAICS code in our list, or create a placeholder for unknown codes
  const autocompleteValue = useMemo(() => {
    if (value === null || value === undefined) return null;
    const found = naicsCodes.find((naicsCode) => naicsCode.code === value);
    if (found) return found;
    // If value exists but isn't in our list, create a placeholder entry
    return { code: value, description: "Unknown NAICS Code" };
  }, [value]);

  // Include the current value in options if it's not in the standard list
  const options = useMemo(() => {
    if (
      value !== null &&
      value !== undefined &&
      !naicsCodes.some((nc) => nc.code === value)
    ) {
      return [
        { code: value, description: "Unknown NAICS Code" },
        ...naicsCodes,
      ];
    }
    return naicsCodes;
  }, [value]);

  const getLabel = (naicsCode: NaicsCode | null) => {
    return naicsCode
      ? `${naicsCode?.description} (${naicsCode?.code})`.trim()
      : "";
  };

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    return (
      <DisplayOnlyField
        text={getLabel(autocompleteValue)}
        label={"NAICS Code"}
      />
    );
  }

  return (
    <>
      <Autocomplete
        ref={ref}
        options={options}
        noOptionsText="Search for a NAICS Code"
        value={autocompleteValue}
        onChange={(_, val) => onChange(val?.code ?? null)}
        getOptionLabel={getLabel}
        renderOption={(props, naicsCode) => (
          <li {...props}>
            {naicsCode.description}&nbsp;
            <Typography color="gray">({naicsCode.code})</Typography>
          </li>
        )}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!error}
            label="NAICS Code"
            variant="outlined"
            autoComplete="off"
            inputProps={{
              ...params.inputProps,
              autoComplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: <>{params.InputProps.endAdornment}</>,
            }}
          />
        )}
      />
      <ErrorDisplay error={error} />
    </>
  );
});
